/**
 * Converts an enum Record to a CheckboxGroupOption array.
 * Automatically maps all record entries.
 * Any changes to the enum or record will be automatically reflected.
 *
 * @template T - The enum type (number or string)
 * @param enumRecord - The Record that maps enum values to their labels
 * @param options - Filter and sorting options
 * @param options.sortByLabel - If true, sorts options alphabetically by labels
 * @param options.includeValues - Array of values to include. If set, only these values will be included
 * @param options.excludeValues - Array of values to exclude. If set, these values will be excluded
 * @returns Array of formatted options with value (type T) and label (string)
 *
 * @example
 * ```typescript
 * enum Status {
 *   Active = 0,
 *   Inactive = 1
 * }
 *
 * const STATUS_RECORD: Record<Status, string> = {
 *   [Status.Active]: 'Ativo',
 *   [Status.Inactive]: 'Inativo'
 * };
 *
 * // Without filters
 * const allOptions = enumRecordToCheckboxOptions(STATUS_RECORD);
 * // Result: [
 * //   { value: 0, label: 'Ativo' },
 * //   { value: 1, label: 'Inativo' }
 * // ]
 *
 * // With inclusion filter
 * const activeOnly = enumRecordToCheckboxOptions(STATUS_RECORD, { includeValues: [0] });
 * // Result: [{ value: 0, label: 'Ativo' }]
 *
 * // With exclusion filter
 * const withoutInactive = enumRecordToCheckboxOptions(STATUS_RECORD, { excludeValues: [1] });
 * // Result: [{ value: 0, label: 'Ativo' }]
 *
 * // With sorting
 * const sorted = enumRecordToCheckboxOptions(STATUS_RECORD, { sortByLabel: true });
 * // Result: [
 * //   { value: 0, label: 'Ativo' },
 * //   { value: 1, label: 'Inativo' }
 * // ]
 * ```
 */
//TODO: Importing CheckboxGroupOption from UI library was causing circular dependency issues.
export function enumRecordToCheckboxOptions<T extends number | string>(
  enumRecord: Record<T, string>,
  options?: {
    sortByLabel?: 'alphabetical' | 'includeValuesOrder';
    includeValues?: T[];
    excludeValues?: T[];
    descriptionsRecord?: Record<T, string>;
  }
): { value: T; label: string; description?: string }[] {
  return Object.entries(enumRecord)
    .filter(([key]) => {
      // Converts the key (string from Object.entries) to the correct type T
      const numValue = Number(key);
      const typedValue: T = Number.isNaN(numValue)
        ? (key as T)
        : (numValue as unknown as T);

      if (
        options?.includeValues &&
        !options.includeValues.includes(typedValue)
      ) {
        return false;
      }

      if (
        options?.excludeValues &&
        options.excludeValues.includes(typedValue)
      ) {
        return false;
      }

      return true;
    })
    .map(([key, label]) => {
      // Converts the key to the correct type T
      const numValue = Number(key);
      const typedValue: T = Number.isNaN(numValue)
        ? (key as T)
        : (numValue as unknown as T);

      const result: { value: T; label: string; description?: string } = {
        value: typedValue,
        label: label as string
      };

      if (
        options?.descriptionsRecord &&
        typedValue in options.descriptionsRecord
      ) {
        result.description = options.descriptionsRecord[typedValue];
      }

      return result;
    })
    .sort((a, b) => {
      if (options?.sortByLabel === 'alphabetical') {
        return a.label.toString().localeCompare(b.label.toString());
      } else if (
        options?.sortByLabel === 'includeValuesOrder' &&
        options.includeValues
      ) {
        return (
          options.includeValues.indexOf(a.value) -
          options.includeValues.indexOf(b.value)
        );
      }
      return 0;
    });
}
